import React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid2";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import { useCreate, useInvalidate, useOne } from "@refinedev/core";
import { Show } from "@refinedev/mui";
import { useParams } from "react-router";
import {
  ACTION_LABEL,
  EVIDENCE_RESOURCE,
  FRAMING_LABEL,
  OUTCOME_LABEL,
  TARGET_LABEL,
  TRIGGER_LABEL,
  canReviewEvidence,
  formatScore,
  reviewClock,
  type EvidenceExtensionBasis,
  type EvidenceHoldDetail,
  type EvidenceHoldEvent,
  type EvidenceReviewOutcome,
} from "@/lib/evidence";
import { EvidenceObject } from "./evidence-object";

const OUTCOMES: readonly EvidenceReviewOutcome[] = ["apparent_csam", "not_csam", "needs_counsel"];

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

/** Local calendar day, for `<input type="date">`. */
const isoDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const eventDetailText = (event: EvidenceHoldEvent): string | null => {
  const detail = event.detail;
  if (!detail) {
    return null;
  }
  const parts = [
    detail.outcome ? OUTCOME_LABEL[detail.outcome] : null,
    detail.ncmecReportId ? `report ${detail.ncmecReportId}` : null,
    detail.until ? `until ${formatDateTime(detail.until)}` : null,
    detail.basis ? `basis ${detail.basis.replace("_", " ")}` : null,
    detail.reference ? `ref ${detail.reference}` : null,
    detail.objectIndex !== undefined ? `object ${detail.objectIndex + 1}` : null,
    detail.objects !== undefined ? `${detail.objects} object(s)` : null,
    detail.contentReportId !== undefined ? `report #${detail.contentReportId}` : null,
    detail.removalRequestId !== undefined ? `removal request #${detail.removalRequestId}` : null,
    detail.unpublish ? "takedown requested" : null,
    detail.reason ?? null,
    detail.note ?? null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(" · ") : null;
};

const EventTimeline = ({ events }: { events: EvidenceHoldEvent[] }) => (
  <Timeline sx={{ p: 0, m: 0, "& .MuiTimelineOppositeContent-root": { flex: 0.35, pl: 0 } }}>
    {events.map((event, index) => (
      <TimelineItem key={event.id}>
        <TimelineOppositeContent color="text.secondary" sx={{ fontSize: 12 }}>
          {formatDateTime(event.at)}
        </TimelineOppositeContent>
        <TimelineSeparator>
          <TimelineDot
            variant={event.action === "viewed" || event.action === "note" ? "outlined" : "filled"}
            color={
              event.action === "preserve_failed" ||
              event.action === "cybertipline_failed" ||
              event.action === "cybertipline_unresolved"
                ? "error"
                : event.action === "released"
                  ? "grey"
                  : event.action === "reported" || event.action === "preserved"
                    ? "success"
                    : event.action === "viewed"
                      ? "warning"
                      : "info"
            }
          />
          {index < events.length - 1 && <TimelineConnector />}
        </TimelineSeparator>
        <TimelineContent sx={{ pb: 2 }}>
          <Typography variant="body2">{ACTION_LABEL[event.action] ?? event.action}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {event.actorId === null ? "system" : `user #${event.actorId}`}
          </Typography>
          {eventDetailText(event) && (
            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
              {eventDetailText(event)}
            </Typography>
          )}
        </TimelineContent>
      </TimelineItem>
    ))}
  </Timeline>
);

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Stack spacing={0.25}>
    <Typography variant="overline" color="text.secondary" lineHeight={1.4}>
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Stack>
);

/**
 * One hold (plans/compliance-launch/ncmec.md §10.4–10.6): the decision, the
 * CyberTipline report, extensions and release — and the material itself,
 * each object behind its own reveal. Every write is a POST to a
 * reviewer-only route and lands on the append-only timeline below.
 */
const EvidenceHold = ({ publicId }: { publicId: string }) => {
  const invalidate = useInvalidate();
  const [access, setAccess] = React.useState<"checking" | "granted" | "denied">("checking");
  React.useEffect(() => {
    let current = true;
    void canReviewEvidence().then((granted) => {
      if (current) {
        setAccess(granted ? "granted" : "denied");
      }
    });
    return () => {
      current = false;
    };
  }, []);
  const detail = useOne<EvidenceHoldDetail>({
    resource: EVIDENCE_RESOURCE,
    id: publicId,
    queryOptions: { enabled: publicId !== "" && access === "granted" },
  });
  const { mutate: post, mutation } = useCreate<EvidenceHoldDetail>();

  const today = new Date();
  const [outcome, setOutcome] = React.useState<EvidenceReviewOutcome>("apparent_csam");
  const [reviewNote, setReviewNote] = React.useState("");
  const [reportId, setReportId] = React.useState("");
  const [ncmecNote, setNcmecNote] = React.useState("");
  const [confirmFiling, setConfirmFiling] = React.useState(false);
  const [submittedAt, setSubmittedAt] = React.useState(isoDate(today));
  const [until, setUntil] = React.useState("");
  const [basis, setBasis] = React.useState<EvidenceExtensionBasis>("legal_process");
  const [reference, setReference] = React.useState("");
  const [releaseReason, setReleaseReason] = React.useState("");

  const hold = detail.result;

  const act = (action: string, values: Record<string, string>, message: string, onDone: () => void) => {
    post(
      {
        resource: `${EVIDENCE_RESOURCE}/${encodeURIComponent(publicId)}/${action}`,
        values,
        successNotification: { message, type: "success" },
      },
      {
        onSuccess: () => {
          onDone();
          void invalidate({ resource: EVIDENCE_RESOURCE, invalidates: ["list", "detail"], id: publicId });
        },
      }
    );
  };

  // Nothing is shown until this session's own reviewer check answers: the query
  // cache outlives a sign-out, so a hold opened earlier in this tab by another
  // admin would otherwise render from it while its refetch was still pending.
  if (access !== "granted") {
    return (
      <Show canEdit={false} canDelete={false} isLoading={access === "checking"} title={publicId}>
        {access === "denied" && (
          <Alert severity="error">Only an admin on the safety reviewer list can open a hold.</Alert>
        )}
      </Show>
    );
  }

  if (detail.query.isError) {
    return (
      <Show canEdit={false} canDelete={false} title={publicId}>
        <Alert severity="error">
          Could not load this hold. Only an admin on the safety reviewer list can open one.
        </Alert>
      </Show>
    );
  }

  const active = hold?.status === "active";
  const fileable = active && hold?.reviewOutcome === "apparent_csam";
  const clock = hold?.reviewDueAt ? reviewClock(hold.reviewDueAt) : null;

  return (
    <Show canEdit={false} canDelete={false} isLoading={detail.query.isLoading} title={<span>{publicId}</span>}>
      {hold && (
        <Stack spacing={2}>
          <Alert severity="warning">
            Open this material only here. Never download it, screenshot it, or send it anywhere — including to a
            colleague, to counsel, or to NCMEC by email. Never paste an object path or a CDN URL anywhere. Every
            reveal is recorded.
          </Alert>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={hold.status} color={hold.status === "active" ? "warning" : "default"} />
            <Chip
              variant="outlined"
              label={`preservation ${hold.preserveState}`}
              color={hold.preserveState === "preserved" ? "success" : hold.preserveState === "failed" ? "error" : "warning"}
            />
            {hold.reviewOutcome && <Chip variant="outlined" label={OUTCOME_LABEL[hold.reviewOutcome]} />}
            {clock && <Chip label={`review ${clock.label}`} color={clock.color} />}
          </Stack>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Field label="Trigger">{TRIGGER_LABEL[hold.trigger]}</Field>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Field label="Target">
                {TARGET_LABEL[hold.targetType]} <code>{hold.targetRef}</code>
              </Field>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Field label="Placed">{formatDateTime(hold.placedAt)}</Field>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Field label="Classifier “minor” score">
                {hold.minorScore === null ? "-" : hold.minorScore.toFixed(2)}
              </Field>
            </Grid>
            {/* The picture check's own scores; an upload's check has neither. */}
            {hold.explicitScore != null && (
              <Grid size={{ xs: 12, md: 3 }}>
                <Field label="Classifier “explicit” score">{formatScore(hold.explicitScore)}</Field>
              </Grid>
            )}
            {hold.ageAmbiguousScore != null && (
              <Grid size={{ xs: 12, md: 3 }}>
                <Field label="Classifier “age_ambiguous” score">{formatScore(hold.ageAmbiguousScore)}</Field>
              </Grid>
            )}
            {hold.framing && (
              <Grid size={{ xs: 12, md: 3 }}>
                <Field label="Framing, as the classifier scored it">{FRAMING_LABEL[hold.framing]}</Field>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 3 }}>
              <Field label="Content report">{hold.contentReportId === null ? "-" : `#${hold.contentReportId}`}</Field>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Field label="Removal request">
                {hold.removalRequestId === null ? "-" : `#${hold.removalRequestId}`}
              </Field>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Field label="CyberTipline report">{hold.ncmecReportId ?? "-"}</Field>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Field label="Preserve until">{formatDateTime(hold.preserveUntil)}</Field>
            </Grid>
          </Grid>

          <Divider />
          <Typography variant="h6">Account at hold time</Typography>
          {hold.subjectSnapshot ? (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <Field label="User id">{hold.subjectSnapshot.userId}</Field>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Field label="Name">{hold.subjectSnapshot.name}</Field>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Field label="Email">{hold.subjectSnapshot.email}</Field>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Field label="Age band">{hold.subjectSnapshot.ageBand ?? "unknown"}</Field>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Field label="Account created">{formatDateTime(hold.subjectSnapshot.accountCreatedAt)}</Field>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Field label="SuperTokens id">{hold.subjectSnapshot.supertokensUserId ?? "-"}</Field>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Field label="Captured">{formatDateTime(hold.subjectSnapshot.capturedAt)}</Field>
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No account snapshot — the account was already gone when the hold was placed.
            </Typography>
          )}

          <Divider />
          <Typography variant="h6">Material</Typography>
          {hold.textSnapshot && (
            <Box
              component="pre"
              sx={{ m: 0, p: 1.5, bgcolor: "action.hover", borderRadius: 1, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {hold.textSnapshot}
            </Box>
          )}
          {hold.objects.length > 0 ? (
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              {hold.status === "released" ? (
                <Typography variant="body2" color="text.secondary">
                  Released: the preserved copies were deleted.
                </Typography>
              ) : (
                hold.objects.map((object) => (
                  <EvidenceObject
                    key={object.index}
                    holdId={hold.publicId}
                    index={object.index}
                    contentType={object.contentType}
                    sizeBytes={object.sizeBytes}
                  />
                ))
              )}
            </Stack>
          ) : (
            !hold.textSnapshot && (
              <Typography variant="body2" color="text.secondary">
                No objects were preserved for this hold.
              </Typography>
            )
          )}
          {hold.classifier?.reason && (
            <Typography variant="caption" color="text.secondary">
              Classifier note: {hold.classifier.reason}
            </Typography>
          )}

          <Divider />
          <Typography variant="h6">Decide</Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2">Review outcome</Typography>
                <TextField
                  select
                  size="small"
                  label="Outcome"
                  value={outcome}
                  disabled={!active}
                  onChange={(event) => setOutcome(event.target.value as EvidenceReviewOutcome)}
                >
                  {OUTCOMES.map((value) => (
                    <MenuItem key={value} value={value}>
                      {OUTCOME_LABEL[value]}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Note"
                  multiline
                  minRows={2}
                  value={reviewNote}
                  disabled={!active}
                  onChange={(event) => setReviewNote(event.target.value)}
                  helperText="Why. This is the record counsel reads — describe the decision, never the material."
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={!active || mutation.isPending}
                  onClick={() =>
                    act(
                      "review",
                      reviewNote.trim() ? { outcome, note: reviewNote.trim() } : { outcome },
                      `Recorded: ${OUTCOME_LABEL[outcome]}`,
                      () => setReviewNote("")
                    )
                  }
                >
                  Record outcome
                </Button>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2">CyberTipline report</Typography>
                {hold.cybertiplineAvailable && (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      Files the report with NCMEC from the server, with every preserved object attached. Nothing passes
                      through this browser. It records the report here once NCMEC has it.
                    </Typography>
                    <TextField
                      size="small"
                      label="Note for NCMEC (optional)"
                      multiline
                      minRows={2}
                      value={ncmecNote}
                      disabled={!active || hold.reviewOutcome !== "apparent_csam"}
                      onChange={(event) => setNcmecNote(event.target.value)}
                      helperText="Sent with the report. Describe what you saw, never paste a path or a URL."
                    />
                    {confirmFiling && fileable ? (
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          disabled={!fileable || mutation.isPending}
                          onClick={() =>
                            act(
                              "cybertipline",
                              ncmecNote.trim() ? { note: ncmecNote.trim() } : {},
                              "CyberTipline report filed and recorded",
                              () => {
                                setNcmecNote("");
                                setConfirmFiling(false);
                              }
                            )
                          }
                        >
                          Send it to NCMEC now
                        </Button>
                        <Button size="small" disabled={mutation.isPending} onClick={() => setConfirmFiling(false)}>
                          Cancel
                        </Button>
                      </Stack>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!fileable || mutation.isPending}
                        onClick={() => setConfirmFiling(true)}
                      >
                        Send to NCMEC
                      </Button>
                    )}
                    <Divider flexItem />
                  </>
                )}
                <Typography variant="caption" color="text.secondary">
                  {hold.cybertiplineAvailable
                    ? "Filed in NCMEC's own portal instead? Record it here. One year of preservation runs from the submission."
                    : "Submit through NCMEC's own reporting interface first, then record it here. One year of preservation runs from the submission."}
                </Typography>
                <TextField
                  size="small"
                  label="Report id"
                  value={reportId}
                  disabled={!active || hold.reviewOutcome !== "apparent_csam"}
                  onChange={(event) => setReportId(event.target.value)}
                />
                <TextField
                  type="date"
                  size="small"
                  label="Submitted on"
                  value={submittedAt}
                  disabled={!active || hold.reviewOutcome !== "apparent_csam"}
                  onChange={(event) => setSubmittedAt(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={!active || hold.reviewOutcome !== "apparent_csam" || !reportId.trim() || mutation.isPending}
                  onClick={() =>
                    act(
                      "ncmec-report",
                      { reportId: reportId.trim(), submittedAt },
                      "CyberTipline report recorded",
                      () => setReportId("")
                    )
                  }
                >
                  Record report
                </Button>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2">Extend preservation</Typography>
                <TextField
                  type="date"
                  size="small"
                  label="Until"
                  value={until}
                  disabled={hold.status === "released"}
                  onChange={(event) => setUntil(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  select
                  size="small"
                  label="Basis"
                  value={basis}
                  disabled={hold.status === "released"}
                  onChange={(event) => setBasis(event.target.value as EvidenceExtensionBasis)}
                >
                  <MenuItem value="legal_process">Legal process (§ 2703(f))</MenuItem>
                  <MenuItem value="voluntary">Voluntary retention (§ 2258A(h)(5))</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  label={basis === "legal_process" ? "Request reference (required)" : "Reference (optional)"}
                  value={reference}
                  disabled={hold.status === "released"}
                  onChange={(event) => setReference(event.target.value)}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={
                    hold.status === "released" ||
                    !until ||
                    (basis === "legal_process" && !reference.trim()) ||
                    mutation.isPending
                  }
                  onClick={() =>
                    act(
                      "extend",
                      reference.trim() ? { until, basis, reference: reference.trim() } : { until, basis },
                      "Preservation extended",
                      () => {
                        setUntil("");
                        setReference("");
                      }
                    )
                  }
                >
                  Extend
                </Button>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2">Release</Typography>
                <Typography variant="caption" color="text.secondary">
                  Lifts each copy&apos;s hold and deletes it. The hold and its timeline stay. Nothing is released on
                  a timer — check for legal process or an ongoing matter first.
                </Typography>
                {hold.releaseBlockedBy && hold.status !== "released" && (
                  <Alert severity="info" variant="outlined">
                    {hold.releaseBlockedBy}
                  </Alert>
                )}
                <TextField
                  size="small"
                  label="Reason"
                  value={releaseReason}
                  disabled={hold.status === "released" || hold.releaseBlockedBy !== null}
                  onChange={(event) => setReleaseReason(event.target.value)}
                />
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  disabled={
                    hold.status === "released" ||
                    hold.releaseBlockedBy !== null ||
                    !releaseReason.trim() ||
                    mutation.isPending
                  }
                  onClick={() =>
                    act("release", { reason: releaseReason.trim() }, "Hold released", () => setReleaseReason(""))
                  }
                >
                  Release
                </Button>
              </Stack>
            </Grid>
          </Grid>

          <Divider />
          <Typography variant="h6">Timeline (append-only)</Typography>
          <EventTimeline events={hold.events} />
        </Stack>
      )}
    </Show>
  );
};

/**
 * Keyed by the hold: moving from one hold to another starts from empty drafts
 * and hidden objects, so nothing typed or revealed for one hold reaches the next.
 */
export const EvidenceHoldShow = () => {
  const { id } = useParams<{ id: string }>();
  const publicId = id ?? "";
  return <EvidenceHold key={publicId} publicId={publicId} />;
};
